const toRoundedNonNegativeInt = (value: unknown): number | null => {
  const numeric = Number(value)
  if (!Number.isFinite(numeric) || numeric <= 0) return null
  return Math.max(0, Math.round(numeric))
}

export const pickBestPriceCents = (...candidates: unknown[]): number => {
  let best = 0

  for (const candidate of candidates) {
    const normalized = toRoundedNonNegativeInt(candidate)
    if (normalized !== null && normalized > best) {
      best = normalized
    }
  }

  return best
}

/**
 * A variant's market price. Real prices only: an estimate for an unpriced condition
 * (`estimatedPriceCents`) is deliberately NOT read here. It used to be (as
 * `fallbackPriceCents`, copied from the nearest priced condition), which valued
 * played trade-ins at the Near Mint price wherever the feed had no played price.
 */
export const getVariantPriceCents = (variant?: {
  priceCents?: unknown
  price?: unknown
} | null): number => {
  if (!variant) return 0
  return pickBestPriceCents(variant.priceCents, variant.price)
}

/** Variant line price only (identical now that estimates live in `estimatedPriceCents`). */
export const getVariantDirectPriceCents = (variant?: {
  priceCents?: unknown
  price?: unknown
} | null): number => {
  if (!variant) return 0
  return pickBestPriceCents(variant.priceCents, variant.price)
}
/**
 * Share of the Near Mint price each played condition is estimated at.
 *
 * A COPY of `CONDITION_SHARE_OF_NEAR_MINT` in backend/src/services/business/condition-price-guard.ts
 * (the frontend cannot import backend code); a backend test fails if the two drift apart.
 */
export const CONDITION_SHARE_OF_NEAR_MINT: Readonly<Record<string, number>> = {
  'lightly played': 0.85,
  'moderately played': 0.70,
  'heavily played': 0.55,
  'damaged': 0.40,
}

const CONDITION_ALIASES: Record<string, string> = {
  lp: 'lightly played',
  mp: 'moderately played',
  hp: 'heavily played',
  dmg: 'damaged',
  dm: 'damaged',
}

/**
 * Estimated price of a played condition the feed has no price for: its printing's Near Mint price
 * times the condition's share. Null for Near Mint, unknown conditions, or no Near Mint price.
 * Always shown as an estimate — never used as the variant's market price.
 */
export const estimatePlayedPriceCents = (nearMintCents: unknown, condition: string | null | undefined): number | null => {
  const nearMint = toRoundedNonNegativeInt(nearMintCents)
  if (nearMint === null) return null
  const key = String(condition ?? '').trim().toLowerCase()
  const share = CONDITION_SHARE_OF_NEAR_MINT[CONDITION_ALIASES[key] ?? key]
  if (share === undefined) return null
  return Math.max(1, Math.round(nearMint * share))
}
